import React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Text } from '@mantine/core'

const spacingSm = 'var(--mantine-spacing-sm, 14px)'
const spacingMd = 'var(--mantine-spacing-md, 16px)'
const codeBg = 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))'
const tableBorder = 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))'
const tableHeadBg = 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))'

/** Shared react-markdown component map (GFM tables, lists, code, etc.). */
export const owlbertMarkdownComponents: Components = {
  p: ({ children }: React.ComponentProps<'p'>) => (
    <Text component="p" size="sm" style={{ margin: 0, marginBottom: '1em', lineHeight: 1.7 }}>
      {children}
    </Text>
  ),
  h1: ({ children }: React.ComponentProps<'h1'>) => (
    <Text component="h1" style={{ fontSize: '1.5em', fontWeight: 700, marginTop: '0.75em', marginBottom: '0.5em', lineHeight: 1.3 }}>
      {children}
    </Text>
  ),
  h2: ({ children }: React.ComponentProps<'h2'>) => (
    <Text component="h2" style={{ fontSize: '1.3em', fontWeight: 700, marginTop: '0.75em', marginBottom: '0.4em', lineHeight: 1.35 }}>
      {children}
    </Text>
  ),
  h3: ({ children }: React.ComponentProps<'h3'>) => (
    <Text component="h3" style={{ fontSize: '1.15em', fontWeight: 600, marginTop: '0.6em', marginBottom: '0.35em', lineHeight: 1.4 }}>
      {children}
    </Text>
  ),
  h4: ({ children }: React.ComponentProps<'h4'>) => (
    <Text component="h4" style={{ fontSize: '1.05em', fontWeight: 600, marginTop: '0.5em', marginBottom: '0.3em', lineHeight: 1.4 }}>
      {children}
    </Text>
  ),
  ul: ({ children }: React.ComponentProps<'ul'>) => (
    <Box component="ul" style={{ margin: 0, marginBottom: '1em', paddingLeft: '1.5em', lineHeight: 1.7 }}>{children}</Box>
  ),
  ol: ({ children }: React.ComponentProps<'ol'>) => (
    <Box component="ol" style={{ margin: 0, marginBottom: '1em', paddingLeft: '1.5em', lineHeight: 1.7 }}>{children}</Box>
  ),
  li: ({ children }: React.ComponentProps<'li'>) => (
    <Text component="li" size="sm" style={{ marginBottom: '0.35em' }}>{children}</Text>
  ),
  table: ({ children }: React.ComponentProps<'table'>) => (
    <Box style={{ overflowX: 'auto', marginBottom: '1em', maxWidth: '100%' }}>
      <Box
        component="table"
        style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--mantine-font-size-sm)' }}
      >
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }: React.ComponentProps<'thead'>) => (
    <Box component="thead" style={{ backgroundColor: tableHeadBg }}>{children}</Box>
  ),
  tbody: ({ children }: React.ComponentProps<'tbody'>) => <Box component="tbody">{children}</Box>,
  tr: ({ children }: React.ComponentProps<'tr'>) => (
    <Box component="tr" style={{ borderBottom: `1px solid ${tableBorder}` }}>{children}</Box>
  ),
  th: ({ children }: React.ComponentProps<'th'>) => (
    <Box
      component="th"
      style={{
        padding: '8px 12px',
        textAlign: 'left',
        fontWeight: 600,
        border: `1px solid ${tableBorder}`,
        verticalAlign: 'top',
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }: React.ComponentProps<'td'>) => (
    <Box
      component="td"
      style={{
        padding: '8px 12px',
        border: `1px solid ${tableBorder}`,
        verticalAlign: 'top',
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children, className }: React.ComponentProps<'code'>) => {
    const isInline = !className
    return isInline ? (
      <Text
        component="code"
        style={{
          backgroundColor: codeBg,
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '0.9em',
          fontFamily: 'var(--mantine-font-family-monospace)',
        }}
      >
        {children}
      </Text>
    ) : (
      <Box
        component="pre"
        style={{
          backgroundColor: codeBg,
          padding: `${spacingSm} ${spacingMd}`,
          borderRadius: '4px',
          marginTop: spacingSm,
          marginBottom: spacingSm,
          fontSize: '0.875rem',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          border: `1px solid ${tableBorder}`,
        }}
      >
        <Text component="code" style={{ fontFamily: 'var(--mantine-font-family-monospace)', whiteSpace: 'inherit' }}>
          {children}
        </Text>
      </Box>
    )
  },
  blockquote: ({ children }: React.ComponentProps<'blockquote'>) => (
    <Box
      style={{
        borderLeft: '3px solid light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-3))',
        paddingLeft: '12px',
        marginLeft: 0,
        marginTop: spacingSm,
        marginBottom: spacingSm,
        color: 'var(--mantine-color-dimmed)',
        fontStyle: 'italic',
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box component="hr" style={{ border: 'none', borderTop: '1px solid var(--separator-color)', margin: '1em 0' }} />
  ),
  a: ({ href, children }: React.ComponentProps<'a'>) => (
    <Text
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      style={{ color: 'var(--mantine-color-blue-6)', textDecoration: 'underline' }}
    >
      {children}
    </Text>
  ),
}

export function OwlbertMarkdown({ content }: { content: string }) {
  if (!content.trim()) return null
  return (
    <Box className="owlbert-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={owlbertMarkdownComponents}>
        {content}
      </ReactMarkdown>
    </Box>
  )
}
